import { useCallback, useMemo, useState } from 'react';
import {
  DataGrid,
  renderTextEditor,
  type Column,
  type FillEvent,
  type RowsChangeData,
} from 'react-data-grid';
import 'react-data-grid/lib/styles.css';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@produzionejb/ui';
import { generateMockMpgRows, KNOWN_LOT_CODES, type MpgRow } from './mock-data';

function isQtyInvalid(qty: number | null): boolean {
  return qty === null || qty === undefined || qty < 0;
}

function isLotUnknown(lot: string): boolean {
  return !KNOWN_LOT_CODES.has(lot);
}

const COLUMNS: readonly Column<MpgRow>[] = [
  {
    key: 'lotCode',
    name: 'Lotto',
    width: 170,
    editable: true,
    renderEditCell: renderTextEditor,
    cellClass: (row) => (isLotUnknown(row.lotCode) ? 'pjb-cell-warn' : undefined),
  },
  {
    key: 'productName',
    name: 'Prodotto',
    width: 240,
    editable: true,
    renderEditCell: renderTextEditor,
  },
  {
    key: 'qty',
    name: 'Qty (g)',
    width: 110,
    editable: true,
    renderEditCell: renderTextEditor,
    cellClass: (row) => (isQtyInvalid(row.qty) ? 'pjb-cell-error' : undefined),
  },
  {
    key: 'causale',
    name: 'Causale',
    width: 220,
    editable: true,
    renderEditCell: renderTextEditor,
  },
  {
    key: 'note',
    name: 'Note',
    width: 280,
    editable: true,
    renderEditCell: renderTextEditor,
  },
];

export function MpgGridPocPage() {
  const [rows, setRows] = useState<MpgRow[]>(() => generateMockMpgRows(1000));
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const stats = useMemo(() => {
    let qtyErrors = 0;
    let unknownLots = 0;
    for (const r of rows) {
      if (isQtyInvalid(r.qty)) qtyErrors += 1;
      if (isLotUnknown(r.lotCode)) unknownLots += 1;
    }
    return { qtyErrors, unknownLots };
  }, [rows]);

  const onRowsChange = useCallback((newRows: MpgRow[], data: RowsChangeData<MpgRow>) => {
    // Normalizza il valore di qty quando l'utente edita la cella numerica
    // (textEditor restituisce sempre stringa).
    const next = [...newRows];
    for (const idx of data.indexes) {
      const updated = next[idx];
      if (!updated) continue;
      if (typeof updated.qty === 'string') {
        const parsed = Number(String(updated.qty).replace(',', '.'));
        next[idx] = { ...updated, qty: Number.isFinite(parsed) ? parsed : null };
      }
    }
    setRows(next);
    setSavedAt(new Date());
  }, []);

  // Fill handle: drag del quadratino in basso a destra della cella selezionata
  // copia il valore della sorgente sulle celle target. react-data-grid expone
  // `onFill` nativo (gratis, no Enterprise).
  const onFill = useCallback((args: FillEvent<MpgRow>): MpgRow => {
    return { ...args.targetRow, [args.columnKey]: args.sourceRow[args.columnKey as keyof MpgRow] };
  }, []);

  function handleAppendRows() {
    setRows((prev) => [...prev, ...generateMockMpgRows(50)]);
  }

  function handleInjectErrors() {
    setRows((prev) =>
      prev.map((r, i) => {
        if (i % 50 === 0) return { ...r, qty: -1 };
        if (i % 37 === 0) return { ...r, lotCode: 'INVALID-LOT' };
        return r;
      }),
    );
  }

  return (
    <div className="space-y-4">
      <style>{`
        .pjb-cell-error { background-color: #fee2e2; }
        .pjb-cell-warn  { background-color: #fef3c7; }
      `}</style>

      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">POC — react-data-grid (MPG)</h2>
        <div className="text-sm text-slate-500">
          {savedAt ? `Salvato ${savedAt.toLocaleTimeString()}` : 'Nessuna modifica'}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Statistiche validazione</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4 text-sm">
          <span>
            Totale righe: <strong>{rows.length}</strong>
          </span>
          <span className="text-red-700">
            Qty non valide: <strong>{stats.qtyErrors}</strong>
          </span>
          <span className="text-amber-700">
            Lotti sconosciuti: <strong>{stats.unknownLots}</strong>
          </span>
          <div className="ml-auto flex gap-2">
            <Button variant="secondary" size="sm" onClick={handleAppendRows}>
              +50 righe
            </Button>
            <Button variant="secondary" size="sm" onClick={handleInjectErrors}>
              Inietta errori demo
            </Button>
          </div>
        </CardContent>
      </Card>

      <div style={{ height: 640 }}>
        <DataGrid<MpgRow>
          className="rdg-light"
          columns={COLUMNS}
          rows={rows}
          onRowsChange={onRowsChange}
          rowKeyGetter={(r) => r.id}
          onFill={onFill}
          enableVirtualization
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Validazione POC</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm text-slate-700">
          <p>
            <strong>Edit cella</strong>: click o doppio click su una cella → editor inline.
            Enter / Tab / click fuori per salvare. Esc per annullare.
          </p>
          <p>
            <strong>Fill handle</strong>: seleziona cella, trascina il quadratino in basso a
            destra per copiare il valore sulle celle target (drag-down stile Excel).
          </p>
          <p>
            <strong>Copy/Paste</strong>: Ctrl/Cmd+C sulla cella selezionata, Ctrl/Cmd+V su cella
            target. Multi-cella tramite shift+arrow + Cmd+C/V.
          </p>
          <p>
            <strong>Validazione visibile</strong>: qty negativa → rosso; lotto fuori master →
            giallo. Recalcolato live ad ogni edit.
          </p>
          <p className="text-slate-500">
            Decisione tecnica registrata in <code>docs/decisions/0003-grid-library-final.md</code>.
            Stack: react-data-grid v7.0.0-beta.59 (MIT, React 19, fill handle nativo gratis).
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
